import React, { useMemo } from 'react';

import { pipe, remote, struct } from '@code-expert/prelude';
import type { UserId } from '/imports/domain';
import { GuardRemote } from '/imports/ui/components/GuardRemote';
import { useUserNameCache } from '/imports/ui/hooks/useUserNameCache';

export const userNameHandle = {
  useUserName(userId: UserId, showTitle = true) {
    const { getUserFromCache } = useUserNameCache();
    return useMemo(
      () =>
        pipe(
          getUserFromCache(userId),
          remote.map(struct.get(showTitle ? 'userTitleName' : 'userName')),
        ),
      [getUserFromCache, userId, showTitle],
    );
  },
};

export type UserNameHandle = typeof userNameHandle;

// -------------------------------------------------------------------------------------------------

export interface UserNamePureProps {
  handle: UserNameHandle;
  userId: UserId;
  showTitle?: boolean;
}

export const UserNamePure = React.memo(function UserNamePure({
  handle: { useUserName },
  userId,
  showTitle,
}: UserNamePureProps) {
  return (
    <GuardRemote
      value={useUserName(userId, showTitle)}
      pending={() => ''}
      render={(username) => <span>{username}</span>}
    />
  );
});

// -------------------------------------------------------------------------------------------------

export type UserNameProps = Omit<UserNamePureProps, 'handle'>;

/**
 * Use this if the Name of the user rarely changes. This is valid except during exams.
 */
function UserName(props: UserNameProps) {
  return <UserNamePure handle={userNameHandle} {...props} />;
}

export default UserName;
